import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import React from 'react';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { DataGridModel } from '../model';
import { ModalItem, ModalPageView } from '@cotera/client/app/components/app';
import {
  Icon,
  IconName,
  Text,
  Tooltip,
} from '@cotera/client/app/components/ui';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { OpenEditColumnAction } from '../../dataset/actions/open-edit-column.action';
import { ViewSqlAction } from '../../dataset/actions/view-sql.action';
import { DatasetModel } from '../../dataset/model/dataset.model';
import { useInScopeModel } from '../../../v0/actions/framework';
import { ArtifactTimestamp } from '../../dataset/dataset.edit';

type ModalManager = {
  push: (
    item: ModalItem & {
      onClose?: () => void;
    }
  ) => void;
  close: () => void;
};

export class OpenSettingsAction extends BaseAction<{}, { t: string }[]> {
  title = 'Open settings';
  description = 'Open settings';
  inputSchema = z.object({});
  group = 'Data Grid';
  icon = 'settings';

  constructor(private readonly modalManager: ModalManager) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(DataGridModel);
  }

  async execute(_: z.infer<typeof this.inputSchema>, context: ExecuteContext) {
    const dataGrid = context.targetedModelOfType(DataGridModel);
    Assert.assert(dataGrid !== null, 'Data grid model not found');

    this.modalManager.push({
      view: () => (
        <SettingsView model={dataGrid} modalManager={this.modalManager} />
      ),
    });

    return ok({
      next: true,
    });
  }
}

const SettingsView: React.FC<{
  model: DataGridModel;
  modalManager: ModalManager;
}> = ({ model, modalManager }) => {
  const viewSql = useAction(ViewSqlAction);
  const datasetModel = useInScopeModel(DatasetModel, model.id);

  const columns = useWatchableValue(model.columns);
  const visibleColumns = columns.filter((c) => c.status === 'visible');
  const hiddenColumns = columns.filter((c) => c.status === 'hidden');

  const primaryKeyExpr =
    datasetModel !== null
      ? (() => {
          const config = datasetModel.config.snapshot();
          return config?.pk !== undefined && config?.pk !== ''
            ? config.pk
            : null;
        })()
      : null;

  return (
    <Tabs defaultValue="columns" className="h-full">
      <ModalPageView
        title={
          <div className="flex items-center gap-2 w-full">
            <TabsList>
              <TabsTrigger value="columns">Columns</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
            </TabsList>
          </div>
        }
        className="px-4 py-4"
      >
        <TabsContent value="columns" className="max-h-[60vh] overflow-y-auto">
          <DndProvider backend={HTML5Backend}>
            {visibleColumns.length > 0 && (
              <SettingsSection
                title="Visible Columns"
                className={hiddenColumns.length > 0 ? 'mb-4' : ''}
              >
                <ul className="flex flex-col gap-2">
                  {visibleColumns.map((c, index) => (
                    <Column
                      key={c.key}
                      model={model}
                      index={index}
                      column={c}
                      modalManager={modalManager}
                    />
                  ))}
                </ul>
              </SettingsSection>
            )}
            {hiddenColumns.length > 0 && (
              <SettingsSection title="Hidden Columns">
                <ul className="flex flex-col gap-2">
                  {hiddenColumns.map((c, index) => (
                    <Column
                      key={c.key}
                      model={model}
                      index={index}
                      column={c}
                      modalManager={modalManager}
                    />
                  ))}
                </ul>
              </SettingsSection>
            )}
          </DndProvider>
        </TabsContent>
        <TabsContent
          value="actions"
          className="flex flex-col gap-2 items-start"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await viewSql.execute({});
            }}
          >
            <Icon icon="code-bracket" />
            View SQL
          </Button>
          {primaryKeyExpr !== null && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                modalManager.push({
                  view: () => (
                    <ModalPageView title="Primary Key" className="p-4">
                      <div className="flex flex-col gap-2">
                        <Text.Caption>
                          The primary key expression for this dataset:
                        </Text.Caption>
                        <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
                          <code>{primaryKeyExpr}</code>
                        </pre>
                      </div>
                    </ModalPageView>
                  ),
                });
              }}
            >
              <Icon icon="key" />
              View Primary Key
            </Button>
          )}
          <ArtifactTimestamp model={model} className="pt-2" />
        </TabsContent>
      </ModalPageView>
    </Tabs>
  );
};

const SettingsSection: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Text.Title type="section">{title}</Text.Title>
      {children}
    </div>
  );
};

const Column: React.FC<{
  model: DataGridModel;
  index: number;
  column: {
    key: string;
    title: string;
    status: 'hidden' | 'visible';
    type: string;
    icon: string;
    t: 'llm' | 'eraql' | 'assumption' | 'workflow';
  };
  modalManager: ModalManager;
}> = ({ model, index, column, modalManager }) => {
  const openColumnSettings = useAction(OpenEditColumnAction);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: 'item',
      item: {
        key: column.key,
        index,
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [index, column.key]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'item',
      drop: (item: { key: string; index: number }) => {
        // Reorder the columns array: move the dragged item to the drop index, shifting others accordingly
        const columns = model.columns.snapshot();
        const fromIndex = columns.findIndex((c) => c.key === item.key);
        const toIndex = index;

        if (fromIndex === -1 || fromIndex === toIndex) {
          return;
        }

        const updated = [...columns];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved!);

        model.columns.set(updated);
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [index, column.key]
  );

  const isClickable = column.t !== 'assumption';

  return (
    <li key={column.key + index} className="flex flex-col" ref={drop}>
      <div
        className={cn(
          'w-full grow flex items-center justify-center transition-all ease-in-out duration-200 overflow-hidden',
          isOver
            ? 'h-10 opacity-100 border-dashed border-divider border bg-indigo-50'
            : 'h-0 opacity-0'
        )}
      >
        <Text.Caption>Drop here to move</Text.Caption>
      </div>
      <div className="justify-between items-center flex">
        <div
          className={cn(
            'flex items-center space-x-2 overflow-hidden',
            isDragging ? 'opacity-50' : ''
          )}
        >
          <Button
            ref={drag}
            className="pl-0! pt-0! pb-0! shrink-0"
            variant="ghost"
            size="icon-sm"
          >
            <Icon icon="drag-handle" className="shrink-0" />
          </Button>
          <Icon icon={column.icon as IconName} className="shrink-0" />
          <Text.Span
            onClick={async () => {
              if (isClickable) {
                await openColumnSettings.execute({
                  name: column.key,
                });
                modalManager.close();
              }
            }}
            ref={preview}
            className={cn(
              'text-ellipsis cursor-pointer',
              column.status === 'hidden' ? 'line-through' : '',
              isClickable ? 'hover:text-primary-text' : ''
            )}
          >
            {column.title}
          </Text.Span>
          <Tooltip tooltipContent={column.type} side="top">
            <div className="w-fit grow-0">
              <Text.Caption className="text-ellipsis truncate">
                {column.type}
              </Text.Caption>
            </div>
          </Tooltip>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={() => {
            if (column.status === 'hidden') {
              model.columns.set(
                model.columns
                  .snapshot()
                  .map((c) =>
                    c.key === column.key ? { ...c, status: 'visible' } : c
                  )
              );
            } else {
              model.columns.set(
                model.columns
                  .snapshot()
                  .map((c) =>
                    c.key === column.key ? { ...c, status: 'hidden' } : c
                  )
              );
            }
          }}
        >
          <Icon
            icon={column.status === 'hidden' ? 'eye-slash' : 'eye'}
            className="shrink-0"
          />
        </Button>
      </div>
    </li>
  );
};
